import { inject, Injectable } from '@angular/core';
import { NATIVE_IMAGE_EXTENSIONS, PreviewData } from '../interfaces/file-preview.interface';
import { FILE_PREVIEW_PROVIDER } from './file-preview-provider.interface';

/**
 * Servizio generico per la gestione delle anteprime file.
 * Delega al provider iniettato (IFilePreviewProvider) le chiamate effettive.
 */
@Injectable()
export class FilePreviewService {
  #provider = inject(FILE_PREVIEW_PROVIDER);

  /**
   * Carica l'anteprima di un file e restituisce i dati strutturati.
   * @param fileType tipo di preview restituito da getFileTypeAllowed (es. 'pdf', 'image', 'xls')
   * @param fileId identificativo del file (sha256 o path relativo)
   * @param fileName nome originale del file, usato per distinguere immagini native da convertite
   */
  async loadPreview(fileType: string, fileId: string): Promise<PreviewData> {
    // getFileTypeAllowed ora restituisce l'estensione reale per le immagini (es. 'png', 'jpg')
    const isNativeImage = NATIVE_IMAGE_EXTENSIONS.includes(fileType);
    if (isNativeImage) {
      const result = await this.#provider.retrievePreview('raw_image', fileId);
      // FileSystem provider restituisce un Blob (bytes raw), AI provider restituisce { images: [] }
      if (result instanceof Blob) {
        return { type: 'image', images: [URL.createObjectURL(result)] };
      }
      let images = Array.isArray(result) ? result : result?.images || [];
      return { type: 'image', images };
    }

    const preview = await this.#provider.retrievePreview(fileType, fileId);

    switch (fileType) {
      case 'pdf':
        if (preview instanceof Blob) {
          return { type: 'pdf', pdfBlob: preview };
        }
        // Il server restituisce direttamente un array di URL quando il PDF viene convertito in immagini
        return {
          type: 'pdf',
          images: Array.isArray(preview) ? preview : preview?.images || [],
        };

      case 'doc':
      case 'docx': {
        if (preview instanceof Blob) {
          return { type: 'doc', wordBlob: preview };
        }
        return { type: 'doc', images: preview?.images || [] };
      }

      case 'image': {
        // Il server restituisce direttamente un array di URL (List<string>),
        // non un oggetto { images: [...] }
        const images = Array.isArray(preview) ? preview : preview?.images || [];
        return { type: fileType === 'image' ? 'image' : 'doc', images };
      }

      case 'xls':
      case 'xlsx':
        return { type: 'excel', excelSheets: preview };

      case 'eml':
      case 'msg':
        return { type: 'email', emailBodyHtml: preview };

      case 'text':
        return { type: 'text', textHtml: preview };

      default:
        return { type: 'unsupported' };
    }
  }

  /**
   * Recupera il contenuto testuale (OCR) di un file.
   */
  async retrieveTextContent(fileId: string): Promise<string> {
    const res = await this.#provider.retrieveTextContent(fileId);
    return res?.textContent || 'Nessun testo estratto.';
  }

  /**
   * Scarica un file e lo salva o apre nel browser.
   */
  async downloadFile(fileId: string, fileName: string): Promise<void> {
    const blob = await this.#provider.downloadFile(fileId);
    const fileURL = URL.createObjectURL(blob);
    if (blob.type && blob.type.toString() === 'application/pdf') {
      window.open(fileURL, '_blank');
    } else {
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.setAttribute('style', 'display: none');
      a.href = fileURL;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(fileURL);
      a.remove();
    }
  }
}
